import React, { useEffect, useState } from "react";
import { useFloorFeed } from "./useFloorFeed";
import { AGENTS, FloorAvatar } from "./FloorAvatar";
import { FloorChart } from "./FloorChart";
import { ScottDesk, JimmyDesk, FundaDesk, PrakashDesk, CramsDesk, DebateDesk, GenieDesk } from "./FloorDesks";

const NODES = [
  { id: "scott", agents: ["scott"] }, { id: "jimmy", agents: ["jimmy"] }, { id: "funda", agents: ["funda"] },
  { id: "prakash", agents: ["prakash"] }, { id: "crams", agents: ["crams"] },
  { id: "debate", agents: ["london", "tariq", "jimmycool"] }, { id: "genie", agents: ["genie"] },
];
const usd = (v) => (v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`);

const Stream = ({ text }) => {
  const words = (text || "").split(" "), [n, setN] = useState(0);
  useEffect(() => { setN(0); const t = setInterval(() => setN((x) => Math.min(words.length, x + 1)), 70); return () => clearInterval(t); }, [text, words.length]);
  return <p className="text-lg text-cyan-50 leading-snug line-clamp-3" data-testid="nexus-stream">{words.slice(0, n).join(" ")}</p>;
};

const NexusBoard = ({ ev, active }) => {
  if (!ev) return null;
  const p = ev.payload;
  return (
    <div className={`nx-hud rounded-3xl p-4 grid grid-cols-[auto_1fr_auto_minmax(0,0.9fr)] gap-5 items-start transition-shadow duration-500 ${active ? "shadow-[0_0_28px_#00F0FF55]" : ""}`} data-testid="floor-node-nexus">
      <div className="flex items-center gap-3"><FloorAvatar agent="nexus" size={56} glow={active} /><div><p className="nx-orbitron font-black text-[#00F0FF]">NEXUS</p><p className="text-xs text-cyan-200/50">Floor Chief</p></div></div>
      <Stream text={p.text} />
      <div className="nx-mono text-sm space-y-1" data-testid="nexus-gates">
        <p className="text-cyan-200/60">GATES <b className="text-rose-300">SHORT {p.gates?.short}</b> · <b className="text-emerald-300">LONG {p.gates?.long}</b></p>
        <p className="text-cyan-200/60" data-testid="nexus-pnl">DAY P&L <b className={p.day_pnl < 0 ? "text-rose-400" : "text-emerald-400"}>{usd(p.day_pnl)}</b> / cap -{usd(p.loss_cap)}</p>
        <div className="h-2 w-48 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-rose-400" style={{ width: `${Math.min(100, (Math.abs(Math.min(0, p.day_pnl || 0)) / (p.loss_cap || 1)) * 100)}%` }} /></div>
      </div>
      <div className="space-y-1 max-h-28 overflow-hidden" data-testid="nexus-actions">
        {(p.actions || []).map((a, i) => (
          <p key={i} className="text-xs text-cyan-100/80 line-clamp-1"><b className={a.type === "exit" ? "text-rose-300" : "text-amber-300"}>{(a.type || "").toUpperCase()}</b> {a.why}</p>))}
      </div>
    </div>
  );
};

const deskFor = (node, s) => {
  const last = (a) => (s.byAgent[a] || []).at(-1);
  if (node.id === "debate") {
    const ev = node.agents.map(last).filter(Boolean).sort((a, b) => a.t_ms - b.t_ms).at(-1);
    return ev && <DebateDesk byAgent={s.byAgent} ticker={ev.payload.ticker} />;
  }
  const ev = last(node.id);
  if (!ev) return null;
  const C = { scott: ScottDesk, jimmy: JimmyDesk, funda: FundaDesk, prakash: PrakashDesk, crams: CramsDesk }[node.id];
  return node.id === "genie" ? <GenieDesk ev={ev} list={s.byAgent.genie || []} /> : <C ev={ev} />;
};

const chip = (node, s) => {
  const ev = node.agents.map((a) => (s.byAgent[a] || []).at(-1)).filter(Boolean).sort((a, b) => a.t_ms - b.t_ms).at(-1);
  const p = ev?.payload || {};
  return p.ticker ? `${p.ticker}${p.verdict ? ` · ${p.verdict}` : ""}` : p.note || "waiting";
};

const Node = ({ node, s }) => {
  const on = node.agents.includes(s.active), a = AGENTS[node.agents[0]];
  const label = node.id === "debate" ? "London vs Tariq · Jimmy Cool" : `${a.name} · ${a.role}`;
  return (
    <div className={`rounded-2xl border px-3 py-2 transition-all duration-500 ${on ? "flex-1 min-h-0 bg-white/[0.04]" : "shrink-0"}`}
      style={{ borderColor: on ? a.color : "#ffffff14", boxShadow: on ? `0 0 22px ${a.color}55` : "none" }} data-testid={`floor-node-${node.id}`} data-active={on}>
      <div className="flex items-center gap-2">
        {node.agents.map((ag) => <FloorAvatar key={ag} agent={ag} size={on ? 40 : 30} glow={s.active === ag} />)}
        <p className="nx-orbitron text-sm font-bold tracking-wider" style={{ color: a.color }}>{label}</p>
        <span className="ml-auto nx-mono text-xs text-cyan-200/50 truncate max-w-[45%]">{chip(node, s)}</span>
      </div>
      {on && <div className="mt-2 min-h-0 overflow-hidden" data-testid={`floor-desk-${node.id}`}>{deskFor(node, s)}</div>}
    </div>
  );
};

export const FloorPipeline = () => {
  const s = useFloorFeed();
  const ticker = s.last?.payload?.ticker || Object.keys(s.candles)[0];
  const chart = s.candles[ticker];
  return (
    <div className="h-full grid grid-rows-[auto_minmax(0,1fr)] gap-4" data-testid="tv-scene-floor">
      <NexusBoard ev={(s.byAgent.nexus || []).at(-1)} active={s.active === "nexus"} />
      <div className="grid grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-4 min-h-0">
        <div className="flex flex-col gap-2 min-h-0" data-testid="floor-pipeline">{NODES.map((n) => <Node key={n.id} node={n} s={s} />)}</div>
        <div className="nx-hud rounded-3xl p-4 flex flex-col min-h-0" data-testid="floor-center">
          <div className="flex items-center justify-between mb-2">
            <p className="nx-orbitron font-black text-2xl text-cyan-50" data-testid="floor-center-ticker">{ticker || "—"}</p>
            <span className="nx-mono text-xs tracking-widest text-cyan-200/50" data-testid="floor-mode">{s.mode.toUpperCase()} · {AGENTS[s.active]?.name || ""}</span>
          </div>
          <div className="flex-1 min-h-0">{chart && <FloorChart key={ticker} data={chart} height={420} testid="floor-center-chart" />}</div>
        </div>
      </div>
    </div>
  );
};
